import { axisBottom, axisLeft, max, min, select } from 'd3'
import { scaleLinear, scaleLog } from 'd3-scale'
import { useEffect, useRef, useState } from 'react'
import './scatterChartWithLogAxis.css'
import {
  formatNumberOnUnitMeasure,
  formatUnitMeasure,
} from '../../../../common/utils/helper'
import { COLOR_MAP } from '../../../../common/utils/theme'
import { AxisValues, ChartElements } from '../../../../common/utils/enum'
import * as d3 from 'd3'
import CorrelationLegend from '../CorrelationLegend'
import useCloseOnOutsideClick from '../../../../common/hooks/useCloseOnOutsideClick'
import { toJpeg, toPng, toSvg } from 'html-to-image'
import { useReactToPrint } from 'react-to-print'
import {
  getColorForCorrelation,
  getMinorGridlinesForLogAxis,
} from '../../common/helper'
import NoDataImage from '../../../../common/components/NoDataImage'

const ScatterChartWithLogAxis = ({
  chartOptions,
  noDataImageURL,
  axisValues,
  axisValuesParams,
  secondIndicatorAxisValues,
  secondIndicatorAxisValuesParams,
  decimals,
  secondIndicatorDecimals,
  indicatorSpecificRegions,
  selectedOnlyShow,
}) => {
  const scatterChartRef = useRef(null)

  const [showMenu, setShowMenu] = useState(false)
  const [isFullScreenMode, setIsFullScreenMode] = useState(false)
  const scatterChartDownloadMenuRef = useRef(null)

  useCloseOnOutsideClick(scatterChartDownloadMenuRef, () => setShowMenu(false))

  var tooltip

  const { series } = chartOptions
  const noData =
    !series ||
    series?.length === 0 ||
    series?.every((item) => item?.data?.length === 0)

  useEffect(() => {
    if (
      scatterChartRef?.current &&
      chartOptions &&
      chartOptions.series &&
      chartOptions.series.length > 0
    ) {
      buildScatterChart()
    }
  }, [scatterChartRef?.current, chartOptions])

  const buildScatterChart = () => {
    let xAxisValues = chartOptions?.series?.flatMap((seriesObj) =>
      seriesObj.data.map((item) => item.x)
    )
    let yAxisValues = chartOptions?.series?.flatMap((seriesObj) =>
      seriesObj.data.map((item) => item.y)
    )

    const updatedXAxisValues = xAxisValues
      // TO-DO: hope d<=0 condition need not be handled as log scale might not be applicable to 0 or negative numbers.
      // .map((d) => (d !== null && d <= 0 ? 1e-2 : d))
      .filter(
        (d) =>
          d !== null &&
          d !== undefined &&
          d !== '' &&
          d !== 'null' &&
          d !== 'NaN'
      )
    const updatedYAxisValues = yAxisValues
      //   // TO-DO: hope d<=0 condition need not be handled as log scale might not be applicable to 0 or negative numbers.
      //   .map((d) => (d !== null && d <= 0 ? 1e-2 : d))
      .filter(
        (d) =>
          d !== null &&
          d !== undefined &&
          d !== '' &&
          d !== 'null' &&
          d !== 'NaN'
      )
    const finalData = chartOptions?.series?.map((seriesObj) => {
      let data = seriesObj.data.map((item, index) => {
        return { ...item, regionId: seriesObj.regionId }
      })
      data = data.filter(
        (item) =>
          item.y !== null &&
          item.y !== undefined &&
          item.y !== '' &&
          item.y !== 'null' &&
          item.y !== 'NaN'
      )
      return {
        name: seriesObj.name,
        regionId: seriesObj.regionId,
        data: data,
        color: seriesObj.color,
      }
    })
    const margin = { top: 20, right: 40, bottom: 40, left: 40 }
    const width =
      document?.querySelector?.('.tui__chart_content')?.clientWidth -
      margin.left -
      margin.right
    const height = 400

    select(scatterChartRef.current).select('svg').remove()

    // Append SVG to the chart container
    const svg = select(scatterChartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},0)`)

    // x-axis scale
    let xScale = ''
    const minValue = min(updatedXAxisValues)
    const maxValue = max(updatedXAxisValues)
    if (axisValues === AxisValues.Logarithmic) {
      xScale = scaleLog()
        .domain([minValue, maxValue])
        .range([margin.left, width])
        .base(Math.E)
        .nice()
    } else if (
      axisValues === AxisValues.Exponential &&
      axisValuesParams &&
      axisValuesParams !== '10'
    ) {
      xScale = scaleLog()
        .domain([min(updatedXAxisValues), max(updatedXAxisValues)])
        .range([margin.left, width])
        .base(Number(axisValuesParams))
        .nice()
    } else if (
      axisValues === AxisValues.Exponential &&
      (!axisValuesParams || axisValuesParams === '10')
    ) {
      xScale = scaleLog()
        .domain([min(updatedXAxisValues), max(updatedXAxisValues)])
        .range([margin.left, width])
        .nice()
    } else {
      xScale = scaleLinear()
        .domain([min(updatedXAxisValues), max(updatedXAxisValues)])
        .range([margin.left, width])
        .nice()
    }

    // y-axis scale
    let yScale = ''
    if (secondIndicatorAxisValues === AxisValues.Logarithmic) {
      yScale = scaleLog()
        .domain([min(updatedYAxisValues), max(updatedYAxisValues)])
        .range([height, margin.top])
        .base(Math.E)
        .nice()
    } else if (
      secondIndicatorAxisValues === AxisValues.Exponential &&
      secondIndicatorAxisValuesParams &&
      secondIndicatorAxisValuesParams !== '10'
    ) {
      yScale = scaleLog()
        .domain([min(updatedYAxisValues), max(updatedYAxisValues)])
        .range([height, margin.top])
        .base(Number(secondIndicatorAxisValuesParams))
        .nice()
    } else if (
      secondIndicatorAxisValues === AxisValues.Exponential &&
      (!secondIndicatorAxisValuesParams ||
        secondIndicatorAxisValuesParams === '10')
    ) {
      yScale = scaleLog()
        .domain([min(updatedYAxisValues), max(updatedYAxisValues)])
        .range([height, margin.top])
        .nice()
    } else {
      yScale = scaleLinear()
        .domain([min(updatedYAxisValues), max(updatedYAxisValues)])
        .range([height, margin.top])
        .nice()
    }

    // x-axis
    const xAxis = svg
      .append('g')
      .attr('class', 'log-x-axis')
      .attr('transform', `translate(0, ${height})`)
      .call(
        axisBottom(xScale)
          .tickFormat((d) =>
           formatNumberOnUnitMeasure({
              value: d,
              unitMeasure: chartOptions?.unitMeasure,
              decimals,
            })
          )
          .tickSize(15)
      )
    // y-axis
    const yAxis = svg
      .append('g')
      .attr('class', 'log-y-axis')
      .attr('transform', `translate(${margin.left}, 0)`)
      .call(
        axisLeft(yScale)
          .tickFormat((d) =>
            formatNumberOnUnitMeasure({
              value: d,
              unitMeasure: chartOptions?.secondIndicatorUnitMeasure,
              decimals: secondIndicatorDecimals,
            })
          )
          .tickSize(15)
      )

    // x-axis title
    svg
      .append('text')
      .attr('class', 'axis-title')
      .attr('text-anchor', 'middle')
      .attr('x', margin.left + width / 2)
      .attr('y', height + margin.top + 30)
      .text(
        // `${chartOptions?.indicatorName} hhkjhkjhkjhjk hhkjhkjhkjhjk hhkjhkjhkjhjk hhkjhkjhkjhjk hhkjhkjhkjhjk hhkjhkjhkjhjk hhkjhkjhkjhjk hhkjhkjhkjhjk`
        chartOptions?.indicatorName
      )
      .call(wrap, width - margin.left)

    // y-axis title
    svg
      .append('text')
      .attr('class', 'axis-title')
      .attr('text-anchor', 'middle')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 20)
      .attr('x', -(height / 2))
      .text(chartOptions?.secondIndicatorName)
      .call(wrap, height)

    xAxis.selectAll('.tick text').attr('fill', (d, index) => {
      // hide last ticks on x axis to remove look and feel like a box border
      // let color =
      //   index === xScale.ticks().length > 2 && xScale.ticks().length - 1
      //     ? 'none'
      //     : 'rgba(0, 0, 0, 0.6)'
      let color = 'rgba(0, 0, 0, 0.6)'
      // hide minor tick numbers
      color = getColorForCorrelation(
        d,
        axisValues,
        axisValuesParams,
        'rgba(0,0,0,0.6)',
        ''
      )
      return color
    })
    yAxis.selectAll('.tick text').attr('fill', (d, index) => {
      // hide last ticks on y axis to remove look and feel like a box border
      // let color =
      //   index === yScale.ticks().length > 2 && yScale.ticks().length - 1
      //     ? 'none'
      //     : 'rgba(0, 0, 0, 0.6)'
      let color = 'rgba(0, 0, 0, 0.6)'
      color = getColorForCorrelation(
        d,
        secondIndicatorAxisValues,
        secondIndicatorAxisValuesParams,
        'rgba(0,0,0,0.6)',
        ''
      )
      return color
    })

    let ygridLineTicks = yScale.ticks()
    if (yScale.ticks().length > 2) {
      // ygridLineTicks.pop()
    }
    if (secondIndicatorAxisValues === AxisValues.Logarithmic) {
      ygridLineTicks = getMinorGridlinesForLogAxis(ygridLineTicks)
    }
    svg
      .append('g')
      .attr('class', 'y-axis-grid')
      .selectAll('line')
      .data(ygridLineTicks)
      .enter()
      .append('line')
      .attr('x1', margin.left)
      .attr('x2', width)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))

    let xgridLineTicks = xScale.ticks()
    if (xScale.ticks().length > 2) {
      // xgridLineTicks.pop()
    }
    if (axisValues === AxisValues.Logarithmic) {
      xgridLineTicks = getMinorGridlinesForLogAxis(xgridLineTicks)
    }

    svg
      .append('g')
      .attr('class', 'x-axis-grid')
      .selectAll('line')
      .data(xgridLineTicks)
      .enter()
      .append('line')
      .attr('stroke', COLOR_MAP[ChartElements.GridLine])
      .attr('y1', height)
      .attr('y2', margin.top)
      .attr('x1', (d) => xScale(d))
      .attr('x2', (d) => xScale(d))

    d3.selectAll('.x-axis-grid line').attr('stroke', (d) => {
      let color =
        d === 0
          ? COLOR_MAP[ChartElements.ZeroLine]
          : COLOR_MAP[ChartElements.GridLine]
      color = getColorForCorrelation(
        d,
        axisValues,
        axisValuesParams,
        COLOR_MAP[ChartElements.GridLine],
        COLOR_MAP[ChartElements.MinorGridLineLogAxis]
      )
      return color
    })

    d3.selectAll('.y-axis-grid line').attr('stroke', (d) => {
      let color =
        d === 0
          ? COLOR_MAP[ChartElements.ZeroLine]
          : COLOR_MAP[ChartElements.GridLine]
      color = getColorForCorrelation(
        d,
        secondIndicatorAxisValues,
        secondIndicatorAxisValuesParams,
        COLOR_MAP[ChartElements.GridLine],
        COLOR_MAP[ChartElements.MinorGridLineLogAxis]
      )
      return color
    })

    // Benchmark plot line
    if (chartOptions?.benchmarkPlotLines?.length > 0) {
      chartOptions.benchmarkPlotLines.forEach((item, index) => {
        if (
          item.value !== 'null' &&
          item.value !== null &&
          item.value !== undefined &&
          item.value !== 'NaN'
        ) {
          svg
            .append('line')
            .attr('class', 'line')
            .attr('x1', xScale(parseFloat(item.value)))
            .attr('x2', xScale(parseFloat(item.value)))
            .attr('y1', 10)
            .attr('y2', height + 10)
            .attr('stroke', COLOR_MAP.benchmark)
            .attr('stroke-width', 1.5)
            .attr('stroke-dasharray', '5 2')

          svg
            .append('text')
            .attr('y', 30)
            .attr('x', () => {
              return width - xScale(parseFloat(item.value)) < 50
                ? // shift text to left side of benchmark line when right side has less space
                  xScale(parseFloat(item.value)) - 60
                : xScale(parseFloat(item.value)) + 10
            })
            .attr('dx', '5px')
            .attr('text-anchor', 'right')
            .text(item.label)
            .attr('class', 'beeswarm-benchmark-label')
            .attr('fill', 'black')
            .style('font-weight', 700)
            .style('font-size', '12px')
            .call(wrapBenchmarkText, width - xScale(parseFloat(item.value)))
        }
      })
    }

    const plotArea = svg.append('g').selectAll('dot')
    finalData.forEach((scatterDataObj) => {
      plotArea
        .data(scatterDataObj.data)
        .enter()
        .append('circle')
        .attr('cx', function (d) {
          return xScale(d.x)
        })
        .attr('cy', function (d) {
          return yScale(d.y)
        })
        .attr('r', 3.75)
        .style('fill', scatterDataObj.color)
        // .style('z-index', 100)
        .attr(
          'class',
          (d) =>
            `data-point ${d.className} ${scatterDataObj.regionId}-data-point`
        )
        .on('mouseover', function (d) {
          // reset all points
          d3.selectAll('.data-point')
            .transition('smooth')
            .duration(200)
            .style('opacity', 1)
            .attr('r', 3.75)
            .style('stroke', (d) => {
              return d.className === 'selected-country-point'
                ? COLOR_MAP.highlight
                : 'none'
            })
            .style('stroke-width', (d) => {
              return d.className === 'selected-country-point' ? '3' : '0'
            })
          // lighten all points
          d3.selectAll('.data-point')
            .transition('smooth')
            .duration(200)
            .style('opacity', 0.2)
          // highlight all points of the region of the hovered point
          d3.selectAll(`.${d.regionId}-data-point`)
            .transition('smooth')
            .duration(200)
            .style('opacity', 1)
            .style('z-index', 1000)
          // increase the hovered point's size
          d3.select(this)
            .transition('smooth')
            .duration(200)
            .attr('r', 6)
            .style('stroke', (d) => {
              return d.className === 'selected-country-point'
                ? COLOR_MAP['highlight']
                : scatterDataObj.color
            })
            .style('stroke-width', '6')
            .style('stroke-opacity', (d) => {
              return d.className === 'selected-country-point' ? '0.6' : '0.4'
            })
            .style('opacity', 1)
            .style('z-index', 1001)

          d3.select('body').selectAll('div.scatter-tooltip').remove()
          tooltip = d3
            .select(scatterChartRef.current)
            .append('div')
            .attr('class', 'scatter-tooltip')
            .html(getTooltipContent(d))
            .style('visibility', 'visible')
            .style('left', d3.mouse(this)[0] - 20 + 'px')
            .style('top', d3.mouse(this)[1] + 20 + 'px')
        })
        .on('mouseleave', function (d) {
          d3.selectAll('.data-point')
            .style('opacity', 1)
            // d3.selectAll('.data-point')
            .attr('r', 3.75)
            .style('stroke', (d) => {
              return d.className === 'selected-country-point'
                ? COLOR_MAP.highlight
                : 'none'
            })
            .style('stroke-width', (d) => {
              return d.className === 'selected-country-point' ? '3' : '0'
            })
            .style('stroke-opacity', (d) => {
              return d.className === 'selected-country-point' ? '1' : '0'
            })

          tooltip.style('visibility', 'hidden')
        })
    })

    d3.selectAll('.selected-country-point')
      .style('stroke', (d) => {
        return COLOR_MAP['highlight']
      })
      .style('stroke-width', (d) => '3')
  }

  function wrap(text, width) {
    text.each(function () {
      const text = d3.select(this)
      const title = text.text()
      const words = text.text().split(/\s+/).reverse()
      let word
      let line = []
      let y = text.attr('y')
      const dy = parseFloat(text.attr('dy') || 0)
      let tspan = text
        .text(null)
        .append('tspan')
        .attr('x', text.attr('x'))
        .attr('y', y)
        .attr('dy', dy + 'em')
        .style('text-anchor', 'middle')

      while ((word = words.pop())) {
        line.push(word)
        tspan.text(line.join(' '))
        if (tspan.node().getComputedTextLength() >= width - 70) {
          // wrapping with ellipsis if text is larger than chart width
          line.push('...')
          tspan.text(line.join(' '))
          // text.append('title').text(title)
          break
        }
      }
      text.append('title').text(title)
    })
  }

  // wrap benchmark text to multi line based on space available
  function wrapBenchmarkText(text, textWidth) {
    text.each(function () {
      const text = d3.select(this)
      const words = text.text().split(/\s+/).reverse()
      let word
      let line = []
      let lineNumber = 0
      const y = text.attr('y')
      const dy = parseFloat(text.attr('dy') || 0)
      let tspan = text
        .text(null)
        .append('tspan')
        .attr('x', text.attr('x'))
        .attr('y', y)
        .attr('dy', dy + 'em')
        .style('text-anchor', 'right')

      while ((word = words.pop())) {
        line.push(word)
        tspan.text(line.join(' '))
        if (tspan.node().getComputedTextLength() > textWidth) {
          line.pop()
          tspan.text(line.join(' '))
          line = [word]
          tspan = text
            .append('tspan')
            .attr('x', text.attr('x'))
            .attr('y', y)
            .attr('dx', '5px')
            .attr('dy', ++lineNumber * 1.1 + dy + 'em')
            .text(word)
        }
      }
    })
  }

  // Download as png
  const exportAsImage = (fileName, type) => {
    const node = document.getElementById('scatter_chart_container')

    if (type === 'png') {
      toPng(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    } else if (type === 'jpeg') {
      toJpeg(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    } else if (type === 'svg') {
      toSvg(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }

  const excludeElements = (node) => {
    const exclusionClasses = ['download-exclude']
    return !exclusionClasses.some((classname) =>
      node.classList?.contains(classname)
    )
  }

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      enterFullscreen()
    } else {
      exitFullscreen()
    }
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullScreenMode(false)
      }
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const enterFullscreen = () => {
    const elem = document.getElementById('scatter_chart_container')
    if (elem.requestFullscreen) {
      elem.requestFullscreen()
    } else if (elem.mozRequestFullScreen) {
      // Firefox
      elem.mozRequestFullScreen()
    } else if (elem.webkitRequestFullscreen) {
      // Chrome, Safari, and Opera
      elem.webkitRequestFullscreen()
    }
    setIsFullScreenMode(true)
  }

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if (document.mozCancelFullScreen) {
      // Firefox
      document.mozCancelFullScreen()
    } else if (document.webkitExitFullscreen) {
      // Chrome, Safari, and Opera
      document.webkitExitFullscreen()
    }
  }

  const handlePrint = useReactToPrint({
    content: () => document.getElementById('scatter_chart_container'),
  })

  const getTooltipContent = (d) => {
    const title = `<div class="tui_chart_tooltip_title" style="font-size: 14px; font-weight: 600; line-height: 16px; color: rgba(0, 0, 0, 0.87); padding-bottom: 4px; margin-bottom: 4px; border-bottom: 1px solid #CED4DE;">${
      d.country
    } ${
      chartOptions?.recentYearSelection ? '' : `, ${chartOptions?.selectedYear}`
    }</div>`
    const content = `<li style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 4px; min-width: 230px;">
        <div class="tooltip_country" style="display: flex; align-items: center;  padding-right: 8px;  ">
                     ${
                       chartOptions?.recentYearSelection
                         ? `${chartOptions?.indicatorName} (${d.xYear})`
                         : chartOptions?.indicatorName
                     }
        </div>
        <span class="tooltip_value" style="font-size: 16px; font-weight: 700; line-height: 20px; color: rgba(0, 0, 0, 0.87);">
         ${formatNumberOnUnitMeasure({
           value: d.x,
           unitMeasure: chartOptions?.selectedUnitMeasure,
           decimals,
         })} ${chartOptions?.unitMultName} ${formatUnitMeasure(
           chartOptions?.selectedUnitMeasure,
           chartOptions?.unitMeasureName
         )}  
        </span>
      </li>
      <li style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 4px;" min-width: 230px;>
        <div class="tooltip_country" style="display: flex; align-items: center;  padding-right: 8px;">
                     ${
                       chartOptions?.recentYearSelection
                         ? `${chartOptions?.secondIndicatorName} (${d.yYear})`
                         : chartOptions?.secondIndicatorName
                     }
        </div>
        <span class="tooltip_value" style="font-size: 16px; font-weight: 700; line-height: 20px; color: rgba(0, 0, 0, 0.87);">
          ${formatNumberOnUnitMeasure({
            value: d.y,
            unitMeasure: chartOptions?.secondIndicatorUnitMeasure,
            decimals: secondIndicatorDecimals,
          })} ${chartOptions?.unitMultName} ${formatUnitMeasure(
            chartOptions?.secondIndicatorUnitMeasure,
            chartOptions?.secondIndicatorUnitMeasureName
          )}  
        </span>
      </li>`

    return `
      <div class="tui_chart_tooltip" style="border: 0.5px solid #CED4DE; background: #FFF; padding: 6px 8px; box-shadow: 4px 4px 4px 0px rgba(0, 0, 0, 0.15); width: auto;">
        ${title}
        <ul style="padding-left: 0; list-style: none; margin: 0;">
          ${content}
        </ul>
      </div>`
  }

  return (
    <div id="scatter_chart_container">
      <div className="tui_comparison_indicator_container tui_chart_dropdown">
        <div className="tui_comparison_title_section">
          <div className="tui_heading_h2_sm">
            <h2>{chartOptions?.title}</h2>
          </div>
          <p className="tui__text_base">{chartOptions?.subtitle}</p>

          {chartOptions?.badges && (
            <div className="tui__badge_container">
              <div className="tui_badge_group">{chartOptions?.badges}</div>
            </div>
          )}
        </div>

       <div
            className="button-container download-exclude"
            ref={scatterChartDownloadMenuRef}
          >
            <div
              className="download-icon"
              onClick={() => setShowMenu((prevState) => !prevState)}
              role="button"
              tabIndex="0"
              aria-haspopup="true"
              aria-expanded={showMenu ? 'true' : 'false'}
              id="scatterChartDownloadMenu"
            >
              <span className="lp lp-bars"></span>
            </div>
            <div
              className={`download-options download-exclude ${
                showMenu ? 'show' : 'hide'
              }`}
              aria-labelledby="scatterChartDownloadMenu"
            >
              <ul>
                <li
                  onClick={() => {
                    handleFullscreen()
                    setShowMenu(false)
                  }}
                >
                  {isFullScreenMode
                    ? 'Exit from full screen'
                    : 'View in full screen'}
                </li>
                <li
                  onClick={() => {
                    {
                      const fileName = `${chartOptions?.indicatorCode}_scatter.png`
                      exportAsImage(fileName, 'png')
                      setShowMenu(false)
                    }
                  }}
                >
                  Download PNG image
                </li>
                <li
                  onClick={() => {
                    {
                      const fileName = `${chartOptions?.indicatorCode}_scatter.jpg`
                      exportAsImage(fileName, 'jpeg')
                      setShowMenu(false)
                    }
                  }}
                >
                  Download JPEG image
                </li>
                <li
                  onClick={() => {
                    const fileName = `${chartOptions?.indicatorCode}_scatter.svg`
                    exportAsImage(fileName, 'svg')
                    setShowMenu(false)
                  }}
                >
                  Download SVG
                </li>
                <li
                  onClick={() => {
                    handlePrint()
                    setShowMenu(false)
                  }}
                >
                  Print
                </li>
              </ul>
            </div>
          </div>

      </div>
      <div className="tui__chart_content">
        {!noData ? (
          <>
            <div
              key="scatter-chart-with-logaxis"
              ref={scatterChartRef}
              className="scatter-chart-with-logaxis"
            ></div>
            <CorrelationLegend
              indicatorSpecificRegions={indicatorSpecificRegions}
              selectedOnlyShow={selectedOnlyShow}
            />
          </>
        ) : (
          <NoDataImage imageURL={noDataImageURL} height="345px" width="100%" />
        )}
      </div>
    </div>
  )
}

export default ScatterChartWithLogAxis
